import { FC, useEffect, useMemo, useRef, useState } from "react";

import "./sentry-instrument";
import { Satos } from "@/app/application/Satos";
import { CssBaseline, ThemeProvider, createTheme } from "@mui/material";
import {
	MutationCache,
	QueryCache,
	QueryClient,
	QueryClientProvider,
} from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";

import { AuthProvider } from "@/auth/AuthProvider";
import { fireAuthError } from "@/auth/authErrorBridge";
import { AuthStatus } from "@/auth/ui";
import useAuthentication from "@/auth/useAuthentication";

import { isHttpError } from "@/shared/api/HttpError";

interface Props {
	children: React.ReactNode;
}

const COLOR_MODE_STORAGE_KEY = "colorMode";
const SYNC_WITH_SYSTEM_STORAGE_KEY = "themeSyncWithSystem";

const getPreferredMode = (): "light" | "dark" => {
	if (typeof window === "undefined") return "light";
	const syncWithSystem = window.localStorage.getItem(
		SYNC_WITH_SYSTEM_STORAGE_KEY,
	);
	const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;

	if (syncWithSystem === "true") {
		return prefersDark ? "dark" : "light";
	}

	const storedMode = window.localStorage.getItem(COLOR_MODE_STORAGE_KEY);
	if (storedMode === "dark" || storedMode === "light") {
		return storedMode;
	}
	return prefersDark ? "dark" : "light";
};

const AuthenticatedUser: FC<Props> = (props) => {
	const context = useAuthentication();
	const loginTriggeredRef = useRef(false);

	useEffect(() => {
		if (!context) return;
		if (!context.isAuthFlowCompleted || context.isFetching || context.user)
			return;
		if (context.accessDenied || context.authError) return;
		if (loginTriggeredRef.current) return;

		loginTriggeredRef.current = true;
		context.login(window.location.href, []);
	}, [context]);

	if (!context || context.isFetching || !context.isAuthFlowCompleted) {
		return <AuthStatus status="checking" />;
	}

	if (context.accessDenied) {
		return <AuthStatus status="denied" />;
	}

	if (context.authError) {
		return <AuthStatus status="error" />;
	}

	if (context.user) {
		return <>{props.children}</>;
	}

	return (
		<AuthStatus
			status="redirecting"
			reason={context?.sessionExpired ? "expired" : undefined}
		/>
	);
};

const queryClient = new QueryClient({
	queryCache: new QueryCache({ onError: fireAuthError }),
	mutationCache: new MutationCache({ onError: fireAuthError }),
	defaultOptions: {
		queries: {
			retry: (failureCount, error) => {
				if (error instanceof DOMException && error.name === "AbortError") {
					return false;
				}
				if (isHttpError(error) && error.status >= 400 && error.status < 500) {
					return false;
				}
				return failureCount < 3;
			},
		},
	},
});

const App = () => {
	const [bootstrapMode, setBootstrapMode] = useState<"light" | "dark">(
		getPreferredMode,
	);
	const bootstrapTheme = useMemo(
		() =>
			createTheme({
				palette: {
					mode: bootstrapMode,
				},
			}),
		[bootstrapMode],
	);

	useEffect(() => {
		if (typeof window === "undefined") return;

		const onStorage = (event: StorageEvent) => {
			if (
				event.key === COLOR_MODE_STORAGE_KEY ||
				event.key === SYNC_WITH_SYSTEM_STORAGE_KEY
			) {
				setBootstrapMode(getPreferredMode());
			}
		};

		const media = window.matchMedia("(prefers-color-scheme: dark)");
		const onSystemThemeChange = () => {
			if (
				window.localStorage.getItem(SYNC_WITH_SYSTEM_STORAGE_KEY) === "true"
			) {
				setBootstrapMode(getPreferredMode());
			}
		};

		window.addEventListener("storage", onStorage);
		media.addEventListener("change", onSystemThemeChange);

		return () => {
			window.removeEventListener("storage", onStorage);
			media.removeEventListener("change", onSystemThemeChange);
		};
	}, []);

	return (
		<ThemeProvider theme={bootstrapTheme}>
			<CssBaseline />
			<QueryClientProvider client={queryClient}>
				<AuthProvider>
					<AuthenticatedUser>
						<Satos />
					</AuthenticatedUser>
				</AuthProvider>
				<ReactQueryDevtools initialIsOpen={false} />
			</QueryClientProvider>
		</ThemeProvider>
	);
};

export default App;
